import React, { createContext, useState, useContext, useEffect } from 'react';
import { TTSType } from '../types';
import { debugLog } from '../utils/logger';

// Define the shape of our cache items
interface CacheItem {
    audioBlob: Blob;
    audioUrl: string;
    timestamp: number;
}

// Define the context type
interface TTSCacheContextType {
    getCachedAudio: (messageId: string, text: string, type: TTSType) => string | null;
    cacheAudio: (messageId: string, text: string, type: TTSType, audioBlob: Blob) => string;
    clearCache: () => void;
}

// Create the context with default values
const TTSCacheContext = createContext<TTSCacheContextType>({
    getCachedAudio: () => null,
    cacheAudio: () => '',
    clearCache: () => { },
});

// Custom hook to use the TTS cache
export const useTTSCache = () => useContext(TTSCacheContext);

// Generate a cache key from message parameters
const generateCacheKey = (messageId: string, text: string, type: TTSType): string => {
    return `tts-${messageId}-${type}-${text.length}`;
};

export const TTSCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // In-memory cache storage
    const [audioCache, setAudioCache] = useState<Record<string, CacheItem>>({});

    // Get audio from cache if available
    const getCachedAudio = (messageId: string, text: string, type: TTSType): string | null => {
        const cacheKey = generateCacheKey(messageId, text, type);
        const cachedItem = audioCache[cacheKey];

        if (cachedItem) {
            debugLog('TTS cache hit', { messageId, type });
            return cachedItem.audioUrl;
        }

        debugLog('TTS cache miss', { messageId, type });
        return null;
    };

    // Store audio in cache
    const cacheAudio = (messageId: string, text: string, type: TTSType, audioBlob: Blob): string => {
        const cacheKey = generateCacheKey(messageId, text, type);

        // Create a URL for the blob
        const audioUrl = URL.createObjectURL(audioBlob);

        // Store in cache
        setAudioCache(prevCache => ({
            ...prevCache,
            [cacheKey]: {
                audioBlob,
                audioUrl,
                timestamp: Date.now()
            }
        }));

        debugLog('Cached TTS audio', { messageId, type, cacheKey });
        return audioUrl;
    };

    // Clear the entire cache
    const clearCache = () => {
        // Revoke all object URLs to prevent memory leaks
        Object.values(audioCache).forEach(item => {
            URL.revokeObjectURL(item.audioUrl);
        });

        setAudioCache({});
        debugLog('TTS cache cleared');
    };

    // Clean up on unmount
    useEffect(() => {
        return () => {
            // Revoke all object URLs to prevent memory leaks
            Object.values(audioCache).forEach(item => {
                URL.revokeObjectURL(item.audioUrl);
            });
        };
    }, [audioCache]);

    return (
        <TTSCacheContext.Provider value={{ getCachedAudio, cacheAudio, clearCache }}>
            {children}
        </TTSCacheContext.Provider>
    );
};
